'use client'
import classnames from 'classnames'

import AnimateContainer from '@/components/AnimateContainer/AnimateContainer'
import Button from '@/components/Button/Button'
import Icon from '@/components/Icon/Icon'
import Link from '@/components/Link/Link'
import LocationActionButtons from '@/components/LocationActionButtons/LocationActionButtons'
import RestaurantOpenClosedBlurbNewFormat from '@/components/RestaurantOpenClosedBlurbNewFormat/RestaurantOpenClosedBlurbNewFormat'

import { DOC_TYPES } from '@/data'

import useBreakpoint from '@/hooks/use-breakpoint'
import useCurrentPage from '@/hooks/use-current-page'
import useI18n from '@/hooks/use-i18n'
import useYelpWaitlist from '@/hooks/use-yelp-waitlist'

import { LOCATION_HERO_INPUT_ID } from '@/sections/LocationHero/LocationHero'

import { extractYelpBusinessId } from '@/utils/yelp'

import { getRestaurantAddressString } from '@/utils/format'
import {
  getMergedTimeRangeHoursWithTimeRangeHolidayHours,
  getIfRestaurantIsOpenNewFormat,
  getPrivateDiningId,
} from '@/utils/restaurant'

import styles from './LocationsListItem.module.scss'

/**
 * Props for the LocationsListItem component.
 * Extends SanityPage data with additional properties needed for list rendering.
 */
type LocationsListItemProps = SanityPage & {
  className?: string
  showPrivateDiningOnly: boolean
  index: number
  totalItems: number
}

/**
 * Renders an individual location item in the Locations list.
 * Displays details like address, hours, open status, and fetches real-time Yelp waitlist
 * data when scrolled into view.
 */
const LocationsListItem = ({
  className,
  index,
  locationData,
  showPrivateDiningOnly,
  slug,
  title,
  totalItems,
}: LocationsListItemProps) => {
  const { i18n, isValidI18nKey } = useI18n()
  const { currentLanguage } = useCurrentPage()
  const { isMobile } = useBreakpoint()

  // 1. Parse and format business metrics
  const businessId = extractYelpBusinessId(locationData?.reservationLinks?.[0]?.url || '') ?? undefined

  const adjustedHours = locationData?.weeklyOpeningHours
    ? getMergedTimeRangeHoursWithTimeRangeHolidayHours(locationData.weeklyOpeningHours, locationData?.holidayHours)
    : undefined

  const isRestaurantOpen =
    locationData?.weeklyOpeningHours && locationData?.timeZone && adjustedHours
      ? getIfRestaurantIsOpenNewFormat(locationData.timeZone, adjustedHours)
      : null

  // 2. Fetch waitlist data when scrolled into view
  const { locationRef, waitlistData, isLoadingWaitlist } = useYelpWaitlist(
    businessId || null,
    locationData?.isReservationOnly ?? false,
    isRestaurantOpen
  )

  // 3. Format view strings and objects
  const addressString = getRestaurantAddressString({
    streetAddress: locationData?.streetAddress,
    city: locationData?.city,
    state: (isValidI18nKey(locationData?.state) && i18n(locationData?.state)) || '',
    postalCode: locationData?.postalCode,
  })

  const locationLink = {
    label: '',
    linkType: 'internal',
    link: {
      _type: DOC_TYPES.LOCATION,
      slug: slug.current,
    },
  }

  const areaTitle = (
    <span className={styles.areaTitle}>
      <span className={styles.areaTitle__at}>&nbsp;{i18n('at')}</span>&nbsp;{locationData?.areaTitle}
    </span>
  )

  if (!locationData || !title || !slug?.current) return null

  return (
    <AnimateContainer
      element="li"
      animationType="fadeInUp"
      className={classnames(styles.LocationsListItem, className, { [styles.isComingSoon]: locationData?.isComingSoon })}
      style={{ zIndex: totalItems - index }}
      setImmediate={isMobile === true}
      ref={locationRef}
    >
      <Link
        className={styles.textSide}
        link={locationLink as SanityLink}
      >
        <div className={styles.titlesContainer}>
          {locationData?.isNew && (
            <div className={styles.newLocationBadge}>
              <Icon name="newLocationBadge" />
            </div>
          )}
          <h1 className={styles.title}>
            {title} {locationData?.areaTitle && <span className={styles.areaTitleContainerMobile}>{areaTitle}</span>}
            <span className={styles.hoverRightIcon}>
              <Icon
                name="caretRight"
                className={styles.hoverRightIcon__icon}
              />
            </span>
          </h1>
          {locationData?.areaTitle && <p className={styles.areaTitleContainerDesktop}>{areaTitle}</p>}
          <div className={styles.openCloseBlurbContainerMobile}>
            <RestaurantOpenClosedBlurbNewFormat
              className={styles.openClosedBlurb}
              weeklyOpeningHours={locationData?.weeklyOpeningHours}
              timeZone={locationData?.timeZone}
              holidayHours={locationData.holidayHours}
              isComingSoon={locationData?.isComingSoon}
              isSoftOpening={locationData?.isSoftOpening}
              isTemporarilyClosed={locationData?.isTemporarilyClosed}
            />
          </div>
        </div>
        <div className={styles.phoneNumberAddressContainer}>
          {locationData?.phoneNumber && (
            <p className={styles.phoneNumber}>{i18n('phoneNumberShort', { phoneNumber: locationData?.phoneNumber })}</p>
          )}
          <p className={styles.address}>
            {addressString
              ? i18n('addressShort', {
                  address: addressString,
                })
              : i18n('newLocationOpeningSoon')}
          </p>
        </div>
      </Link>
      <div className={styles.blurbColumn}>
        <div className={styles.openCloseBlurbContainerDesktop}>
          <RestaurantOpenClosedBlurbNewFormat
            className={styles.openClosedBlurb}
            weeklyOpeningHours={locationData?.weeklyOpeningHours}
            timeZone={locationData?.timeZone}
            holidayHours={locationData.holidayHours}
            isComingSoon={locationData?.isComingSoon}
            isSoftOpening={locationData?.isSoftOpening}
            isTemporarilyClosed={locationData?.isTemporarilyClosed}
          />
        </div>
      </div>
      <div className={styles.ctaSide}>
        {locationData?.isComingSoon ? (
          <Button
            className={styles.comingSoonButton}
            label={i18n('notifyMeWhenOpening')}
            link={locationLink as SanityLink}
            secondary
            icon="caretRight"
            onClick={() => {
              setTimeout(() => {
                const input = document.getElementById(LOCATION_HERO_INPUT_ID)
                if (input) input.focus()
              }, 150)
            }}
          />
        ) : showPrivateDiningOnly ? (
          <Button
            primary
            link={
              {
                label: i18n('details'),
                linkType: 'internal',
                link: {
                  _type: DOC_TYPES.LOCATION,
                  slug: slug.current,
                },
                hash: getPrivateDiningId(currentLanguage as string, slug.current),
              } as SanityLink
            }
            icon="caretRight"
          />
        ) : (
          <LocationActionButtons
            reservationLinks={locationData?.reservationLinks}
            serviceLinks={locationData?.isSoftOpening ? undefined : locationData?.serviceLinks}
            businessId={businessId}
            isReservationOnly={locationData?.isReservationOnly ?? false}
            waitlistData={waitlistData}
            isLoadingWaitlist={isLoadingWaitlist}
            isMobile={isMobile}
            hasCatering={locationData?.hasCatering ?? false}
            cateringLink={locationData?.cateringLink}
            dropdownPosition="bottomRight"
            slug={slug?.current}
            className={styles.locationActionButtons}
          />
        )}
      </div>
    </AnimateContainer>
  )
}

LocationsListItem.displayName = 'LocationsListItem'

export default LocationsListItem
